
import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, LogOut } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileForm } from '@/components/profile/ProfileForm';

interface UserProfileCardProps {
  user: any;
  userProfile: any;
}

const UserProfileCard = ({ user, userProfile }: UserProfileCardProps) => {
  const { toast } = useToast();
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) throw error;
      
      toast({
        title: "로그아웃 성공",
        description: "안녕히 가세요!",
      });
    } catch (error: any) {
      console.error('Error logging out:', error);
      toast({
        variant: "destructive",
        title: "로그아웃 실패",
        description: error.message || "로그아웃 중 오류가 발생했습니다.",
      });
    }
  };

  const handleProfileUpdate = async () => {
    if (user?.id) {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      setIsEditingProfile(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <User size={18} />
            내 프로필
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center mb-4">
            <p className="font-medium">{userProfile?.username || user.email}</p>
            <p className="text-xs text-muted-foreground mt-1">{user.email}</p>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => setIsProfileDialogOpen(true)}
            >
              프로필 보기
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>프로필</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="view" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="view">보기</TabsTrigger>
              <TabsTrigger value="edit">수정</TabsTrigger>
            </TabsList>
            
            <TabsContent value="view" className="mt-4">
              <div className="flex flex-col items-center space-y-4">
                <div className="text-center">
                  <h3 className="font-medium text-lg">{userProfile?.username || "사용자"}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
                </div>
                <div className="w-full">
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-muted-foreground">가입일:</dt>
                      <dd className="text-sm">{new Date(user?.created_at || Date.now()).toLocaleDateString('ko-KR')}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="edit" className="mt-4">
              <div className="space-y-4">
                {user && userProfile && (
                  <ProfileForm 
                    initialUsername={userProfile.username || ""} 
                    userId={user.id}
                    onSuccess={handleProfileUpdate}
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-0">
            <Button 
              variant="destructive" 
              onClick={handleLogout}
              className="w-full sm:w-auto order-1 sm:order-none"
            >
              <LogOut size={16} className="mr-1" />
              로그아웃
            </Button>
            <Button 
              onClick={() => setIsProfileDialogOpen(false)}
              className="w-full sm:w-auto"
            >
              닫기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserProfileCard;
